/**
 * BlogSeoMeta.js
 *
 * SEO + structured data components for the Blog section.
 * Mirrors the exact pattern of YogSeoMeta.js:
 *   - Keyed <Helmet> so React always re-renders on canonical change
 *   - trimDesc() respects word boundaries (155 char cap)
 *   - toSlug() for canonical URL segments
 *   - BreadcrumbList JSON-LD on every page
 *   - BlogPosting JSON-LD on article pages
 *   - Open Graph + Twitter Card on both pages
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";

// ─── Helpers ───────────────────────────────────────────────────────────────────
const toSlug = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Mirrors YogSeoMeta — word-boundary aware trim
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// BlogListMeta — used on the /blog listing page
// ═══════════════════════════════════════════════════════════════════════════════

export const BlogListMeta = ({ posts = [], featuredImgUrl = null }) => {
  const canonical    = `${SITE_URL}/blog`;
  const title        = `Blog — Wisdom, Wellness & Yoga | ${SITE_NAME}`;
  const description  = "Explore insights on yoga philosophy, therapeutic practices, and holistic well-being — written by our experienced instructors.";
  const keywords     = "yoga blog, yoga wellness, pranayama, yoga philosophy, holistic health, yoga therapy";

  // BreadcrumbList
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",  item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog",  item: canonical },
    ],
  };

  // Blog + BlogPosting list schema
  const blogSchema = {
    "@context": "https://schema.org",
    "@type":    "Blog",
    name:        `${SITE_NAME} Blog`,
    description,
    url:         canonical,
    publisher: {
      "@type": "Organization",
      name:    SITE_NAME,
      url:     SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
    blogPost: posts.slice(0, 10).map((p) => ({
      "@type":         "BlogPosting",
      headline:        p.title,
      description:     trimDesc(p.seo?.metaDescription || p.shortDescription),
      author:          { "@type": "Person", name: p.author },
      datePublished:   p.date,
      url:             `${SITE_URL}/blog/${p.sN}`,
      keywords:        p.seo?.keywords?.join(", ") ?? "",
    })),
  };

  // Keyed so Helmet flushes when posts change — mirrors YogSeoMeta fix
  return (
    <Helmet key={canonical}>
      <title>{title}</title>
      <meta name="description"  content={description} />
      <meta name="keywords"     content={keywords} />
      <link rel="canonical"     href={canonical} />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={featuredImgUrl || OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={featuredImgUrl || OG_IMAGE} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
    </Helmet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BlogArticleMeta — used on the /blog/:sN article page
// ═══════════════════════════════════════════════════════════════════════════════

export const BlogArticleMeta = ({ post, heroImg = null }) => {
  if (!post) return null;

  const canonical   = `${SITE_URL}/blog/${post.sN}`;
  const title       = `${post.title} | ${SITE_NAME}`;
  const description = trimDesc(post.seo?.metaDescription || post.shortDescription);
  const keywords    = post.seo?.keywords?.join(", ") ?? "";
  const image       = heroImg || OG_IMAGE;

  // BreadcrumbList — mirrors YogSeoMeta breadcrumb pattern
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",     item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog",     item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: post.category, item: `${SITE_URL}/blog?category=${toSlug(post.category)}` },
      { "@type": "ListItem", position: 4, name: post.title, item: canonical },
    ],
  };

  // BlogPosting structured data
  const articleSchema = {
    "@context":       "https://schema.org",
    "@type":          "BlogPosting",
    headline:          post.title,
    description,
    image,
    author:           { "@type": "Person", name: post.author },
    datePublished:     post.date,
    dateModified:      post.date,
    url:               canonical,
    keywords,
    publisher: {
      "@type": "Organization",
      name:    SITE_NAME,
      url:     SITE_URL,
      logo:    { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    inLanguage:        "en-IN",
    isFamilyFriendly:  true,
  };

  // Keyed on canonical — mirrors YogSeoMeta fix so Helmet re-renders on nav
  return (
    <Helmet key={canonical}>
      <title>{title}</title>
      <meta name="description"  content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical"     href={canonical} />

      {/* Open Graph — article type */}
      <meta property="og:type"                content="article" />
      <meta property="og:site_name"           content={SITE_NAME} />
      <meta property="og:title"               content={title} />
      <meta property="og:description"         content={description} />
      <meta property="og:url"                 content={canonical} />
      <meta property="og:image"               content={image} />
      <meta property="og:image:width"         content="1200" />
      <meta property="og:image:height"        content="630" />
      <meta property="og:locale"              content="en_IN" />
      <meta property="article:published_time" content={post.date} />
      <meta property="article:author"         content={post.author} />
      {post.seo?.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={image} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
    </Helmet>
  );
};
